/**
 * DUAL-ROLE-1-UI — one email, one account, two workspaces.
 *
 * THE WHOLE OF THIS MODULE IS A READ OF `/api/me`. Eligibility comes from
 * `workspace_eligibility {admin, teacher}` and the current view from `active_workspace`, both added
 * by DUAL-ROLE-1-BE. Nothing here derives either from a role string, a URL, or a client guess —
 * which is the point: a switcher rendered from a role would appear for admins who do not teach, and
 * a workspace inferred from the address bar would be a client inventing its own state.
 *
 * AND `active_workspace` IS NOT AUTHORIZATION. Lane 2 says so in the merged branch, in as many
 * words, and it holds here too: this value decides which routes RENDER and where a switch LANDS.
 * Every request is still authorized by the server, per request, from the token. Nothing in this
 * file is allowed to become a scope.
 */

export const WORKSPACE_ADMIN = "admin";
export const WORKSPACE_TEACHER = "teacher";

/** The home route each workspace lands on. Read from the app's own router, never invented here. */
const WORKSPACE_HOME = Object.freeze({
  [WORKSPACE_ADMIN]: "/admin",
  [WORKSPACE_TEACHER]: "/my-workspace",
});

const eligibility = (user) =>
  user && typeof user.workspace_eligibility === "object" && user.workspace_eligibility
    ? user.workspace_eligibility
    : null;

/**
 * Which workspace the user is currently in. Falls back to admin ONLY when the server said so;
 * an absent field is not a licence to guess, so it answers null and the caller renders nothing.
 */
export function activeWorkspace(user) {
  const value = user?.active_workspace;
  return value === WORKSPACE_ADMIN || value === WORKSPACE_TEACHER ? value : null;
}

/**
 * True only when the server says this account may use BOTH workspaces. A plain admin and a plain
 * teacher each get one `true` and one `false`, so neither is offered a switch — BUILD 5, decided
 * here rather than at three call sites.
 */
export function isDualRole(user) {
  const el = eligibility(user);
  return Boolean(el && el[WORKSPACE_ADMIN] === true && el[WORKSPACE_TEACHER] === true);
}

/** The workspace a switch would move to, or null when there is no switch to offer. */
export function otherWorkspace(user) {
  if (!isDualRole(user)) return null;
  const active = activeWorkspace(user);
  if (!active) return null;
  return active === WORKSPACE_ADMIN ? WORKSPACE_TEACHER : WORKSPACE_ADMIN;
}

/** Where a switch to `workspace` lands. Null for anything this module does not recognise. */
export function workspaceHome(workspace) {
  return WORKSPACE_HOME[workspace] || null;
}

/**
 * THE SAFEGUARD, and the whole of the authorization change this goal permits: an eligible dual-role
 * user may reach teacher routes. Nothing wider. It answers a question the route guard asks; it never
 * answers one the server asks.
 */
export function mayReachTeacherRoutes(user) {
  const el = eligibility(user);
  return Boolean(el && el[WORKSPACE_TEACHER] === true);
}
